import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { Formik, type FormikValues, useField } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthenticateMutation } from '../../../api/mutations/authenticate';
import Form from '../../../components/fields/Form';
import LoginContent from '../../../layouts/LoginLayout/LoginContent';
import { useThemeComponents } from '../../../themes';
import useHandleError from '../../../utilities/useHandleError';
import useValidator from '../../../utilities/useValidator';
import validators from '../../../utilities/validators';
import AuthenticateWebAuthn from './AuthenticateWebAuthn';
import { type ActionHandlers } from './index';

export type FormValues = { email: string; password: string; remember?: boolean };

export type AuthenticateStepProps = { actions: ActionHandlers };

type AutheticateStepFieldProps = AuthenticateStepProps & {
    isSubmitting: boolean;
    isValid: boolean;
    values: FormikValues;
};

const StyledLink = styled(Link)`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
    color: var(--ant-primary-color);
`;

const formValidator = validators.compose(
    validators.requiredString('email'),
    validators.validEmail('email'),
    validators.passwordLength('password'),
    validators.requiredString('password')
);

const AuthenticateStepField = ({ actions, isSubmitting, isValid, values }: AutheticateStepFieldProps) => {
    const { t } = useTranslation(['signInPage', 'common']);
    const {
        Button,
        FormFields: { InputField, PasswordField },
    } = useThemeComponents();

    const [{ value: email }, , { setValue: setEmail }] = useField('email');
    const [{ value: password }, , { setValue: setPassword }] = useField('password');

    useEffect(() => {
        setEmail(email.trim());
        setPassword(password.trim());
    }, [email, password, setEmail, setPassword]);

    return (
        <>
            <InputField
                {...t('signInPage:authenticateStep.fields.email', { returnObjects: true })}
                name="email"
                prefix={<UserOutlined />}
                size="large"
                allowClear
                required
            />
            <PasswordField
                {...t('signInPage:authenticateStep.fields.password', { returnObjects: true })}
                name="password"
                prefix={<LockOutlined />}
                size="large"
                allowClear
                required
            />
            <StyledLink state={{ email: values.email }} to="../resetPassword">
                {t('signInPage:authenticateStep.forgotPasswordLink')}
            </StyledLink>
            <AuthenticateWebAuthn actions={actions} />
            <Button
                disabled={!isValid || !values.email || !values.password}
                htmlType="submit"
                loading={isSubmitting}
                size="large"
                type="primary"
                block
            >
                {t('signInPage:authenticateStep.submitBtn')}
            </Button>
        </>
    );
};

const AuthenticateStep = ({ actions }: AuthenticateStepProps) => {
    const { t } = useTranslation(['signInPage', 'common']);

    const [authenticate] = useAuthenticateMutation();
    const state = useLocation().state as { email?: string };
    const rememberedEmail = localStorage.getItem('app:signInFor');

    const onSubmit = useHandleError(
        async ({ remember, ...values }: FormValues) => {
            message.loading({
                type: 'loading',
                content: t('signInPage:authenticateStep.submittingMessage'),
                key: 'primary',
                duration: 0,
            });

            const { data } = await authenticate({ variables: values }).finally(() => {
                message.destroy('primary');
            });

            if (remember) {
                localStorage.setItem('app:signInFor', values.email);
            }

            switch (data.authenticate.__typename) {
                case 'AuthenticationRequiresNewPassword':
                    return actions.moveToPasswordExpired(data.authenticate.token);

                case 'AuthenticationRequiresTOTP':
                    return actions.moveToTOTP(data.authenticate.token);

                case 'AuthenticationRequiresSmsOTP':
                    return actions.moveToSmsOTP(data.authenticate.token, values);

                case 'AuthenticationSuccessful':
                    return actions.completeAuthentication(data.authenticate.token, data.authenticate.user);

                default:
                    // not supported
                    return undefined;
            }
        },
        [authenticate, actions, t]
    );

    const validate = useValidator(formValidator);

    return (
        <LoginContent title={t('signInPage:authenticateStep.title')}>
            <Formik
                initialValues={{
                    email: state?.email || rememberedEmail || '',
                    password: '',
                    remember: !!rememberedEmail,
                }}
                onSubmit={onSubmit}
                validate={validate}
            >
                {({ isSubmitting, isValid, handleSubmit, values }) => (
                    <Form onSubmitCapture={handleSubmit}>
                        <AuthenticateStepField
                            actions={actions}
                            isSubmitting={isSubmitting}
                            isValid={isValid}
                            values={values}
                        />
                    </Form>
                )}
            </Formik>
        </LoginContent>
    );
};

export default AuthenticateStep;
